import { navChange } from '@classic/Foundation/Navigation/Nav'
import { get, post } from '@classic/Foundation/Services/ApiService'
import { refreshLogonState } from './refreshLogonState'
import { queryClient } from '@core/Data/ReactQuery'
import { queryKeys } from '@mobi/account/Utils'
import {
  AuthenticationPath,
  type LogonRequestData,
  type LogonResult,
  type IsAuthenticated,
  type LogonWithMfaRequest,
  type LogonWithMfaResult,
  type RegisterTrustedDeviceRequest,
  ForgetTrustedDeviceRequest,
} from '@mobi/api-types'
import { CognitoUnauthorizedError } from '@core/Areas/Settings/Components/MFASettings/Hooks/useCognitoUserIsAuthenticated'

// MOBI requires capitalised root - work is needed to fix
export const logon = (body: LogonRequestData) =>
  post<LogonResult>({ url: `${AuthenticationPath}/logon`, body })

export const completeLogonWithMfa = (body: LogonWithMfaRequest) =>
  post<LogonWithMfaResult>({ url: `${AuthenticationPath}/complete-logon-with-mfa`, body })

export const postLogout = (): Promise<void> => {
  // Cancel in-flight betAccount requests before logout completes. Without this, a stale 401
  // can arrive while isLoggedIn is still true, causing useIsVerificationAccount in App.tsx
  // to incorrectly navigate to the verification account error page.
  queryClient.cancelQueries({ queryKey: [queryKeys.betAccount] })
  return post({ url: `${AuthenticationPath}/logoff` }).then(() => {
    refreshLogonState()
    navChange('/')
  })
}

export const registerTrustedDevice = async (body: RegisterTrustedDeviceRequest) => {
  try {
    return await post({ url: '/api/mfa/register-trusted-device', body })
  } catch (e) {
    if (e && typeof e === 'object' && 'response' in e && e.response instanceof Response) {
      if (e.response.status == 403) {
        throw new CognitoUnauthorizedError()
      }
    }

    throw e
  }
}

export const forgetDevice = (body: ForgetTrustedDeviceRequest) =>
  post({ url: '/api/mfa/forget-user-device', body })

/** Salted cookie value is sent to server for unsalting */
export const getRememberAccountNumber = (): Promise<number | undefined> =>
  get<{ accountNumber: number }>({
    url: `${AuthenticationPath}/GetAccountNumber`,
  }).then(data =>
    !data || !data.accountNumber || !Number.isInteger(data.accountNumber)
      ? undefined
      : data.accountNumber
  )

export const getIsAuthenticated = () =>
  get<IsAuthenticated>({ url: `${AuthenticationPath}/IsAuthenticated` })

export const getIsLoggedOn = (): Promise<boolean> =>
  getIsAuthenticated().then(data => !!data.accountNumber)
